/**
 * The first address this tab asked for a magic link for, and the capability
 * binding it buys.
 *
 * THE HOLE IT CLOSES. `/auth`'s send reads whatever this tab is holding -- the
 * pending intent, with a space invite token inside it, the stored destination,
 * possibly bearing `?invite=`, and the pending-question claim entries, whose secrets
 * the flow read resolves to question TITLES -- and uploads it with the request, so the
 * minted link carries it back. That is the whole point of the carrier, and it has no
 * notion of WHO the link is for: guest A accepts an invite and abandons the door,
 * B types their own address into the same tab, and A's invitation token is minted
 * into a link delivered to B's inbox. The landing gate names the action for B, so
 * B cannot execute it unknowingly -- but the capability has already left the
 * building, in an email, to an address nobody authorised it for.
 *
 * THE BINDING. The first address a tab sends to is remembered here. A later send to
 * the SAME address (a resend, or a retype in different case) is unchanged. A send
 * to a DIFFERENT address keeps the plain halves -- a GOING_EVENT intent, a plain
 * path -- and strips the capability-bearing parts: the invite token comes off the
 * intent and the secret params come off the destination (see
 * `@lib/magicLink/capabilityPaths` and `stripIntentCapabilities`). The capability stays
 * with the first recipient; everything else still travels. The door's "Use a
 * code instead" exit applies the same rule to the stash it hands `/callback`: to a
 * different address, the tab's own copy is stripped before it leaves. A social chip
 * names no address, so it strips only once the bound address has been turned down
 * (the mark below). The door's mount names no address either, and it re-arms the
 * envelope stripped only once a send from this tab has gone out stripped (the signal
 * below).
 *
 * THE CLAIM ENTRIES ARE BOUND BY THEIR OWN ADDRESS, not by this one. Each entry records the
 * address its row was filed under, and a send carries only the entries filed under the address
 * it goes to (`claimEntriesForRecipient` in `@utils/pendingQuestionClaim`), because this binding
 * is written only when the tab SENDS: a guest who asked and left the door unsent bound nothing,
 * and the next person's send took their secret. Only an entry filed under no address (stored
 * before that field existed) still follows this binding.
 *
 * THE RESIDUAL, and it is the intended side of the trade. This tab cannot tell a
 * typo from a stranger. A visitor who accepted an invite, typed `a@b.co` and
 * corrected it to `a@b.com` gets their ACCEPT_SPACE_INVITE degraded to a plain
 * JOIN_COMMUNITY for the same space and the token dropped -- so typo-then-resend
 * survives intact for plain intents, and on an INVITE flow a corrected typo costs
 * the capability and the visitor reopens the invite link they were sent. A guest who
 * asked a question and corrects a typo here no longer pays that way: their entry is filed
 * under the address they asked with, so the corrected send carries it. Only an entry filed
 * under no address still pays (TECH-540 accepts that cost explicitly); its tab keeps it,
 * and it claims by secret, never by device token, so it publishes only if the guest signs
 * in from that tab under the address the row was filed under; otherwise the question is
 * lost, the loss accepted with TECH-509. That is the direction to err in: a capability is
 * never silently re-addressed, and for an invite the recovery is one link the visitor
 * already has.
 *
 * WHERE IT LIVES. Per-tab like every other value in this flow, AND mirrored into
 * localStorage beside the intent, the destination and the claim entries it binds --
 * because those three travel. `/auth/link` hydrates them into a tab that never sent
 * anything, and every route from there back to the door (the spent-link CTA, the
 * unnamed-link CTA, the failure reveal's fresh-link link, `/callback`'s "Sign in
 * again", logto-ui's expired/consumed bounce) would otherwise land a door holding
 * A's invitation token with no binding at all: a send to B would read the binding as
 * absent, conclude "first recipient", and mint the token into B's inbox in full. During the rollout
 * window -- backend flag off, every flow read 404ing -- that is the ordinary path
 * rather than an edge case. A hydration that storage will not keep fails closed there:
 * `/auth/link` strips what it took delivery of (`hydrateFirstSendAddressFromMagicLinkMirror`).
 *
 * It is cleared on sign-out (`storageCleanup.ts` lists both the key and, via
 * `MAGIC_LINK_MIRROR_KEYS`, the mirror), preserved through `/auth/link`'s
 * account-switch sweep (it is in `AUTH_HANDOVER_KEYS` with the values it guards --
 * a sweep that deleted the binding while keeping the invitation would be the same
 * hole again), and dropped by the door itself on an arrival with nothing pending: a
 * visitor carrying no intent, no destination and no claim entries is starting a new flow,
 * and the previous one's address must not constrain it. The claim is a term in that test
 * for a measured reason -- its flow arrives at the door holding nothing else, so a
 * two-term test cleared the binding on exactly the arrivals it was extended to guard.
 * And `/auth/link` REBINDS it whenever a payload replaces this tab's stash, the envelope's
 * on the mount or the link's own server copy on the confirm (`rebindFirstSendAddress`,
 * #1274): the old binding goes and the one that describes the new payload is written.
 *
 * ONE MORE RESIDUAL, from the shape of the mirror rather than from the rule: a door
 * arrival that mirrors a capability and then never sends leaves that capability on the
 * envelope with a NULL binding by construction, so the first send from any tab that
 * later hydrates it reads as the first recipient. That is the pre-existing
 * abandoned-door shape (the same one the profile-wide mirrors have always had), it is
 * bounded by the 30-minute TTL, and the `/auth/link` gate still names the action to
 * whoever redeems the link. A claim entry filed under an address is out of it: it
 * carries its address through the envelope, and a send carries it to that address only.
 *
 * Deliberately a LEAF module: `@utils/logger` and `@utils/magicLinkMirror`, both leaves
 * themselves.
 */
import { logger } from '@utils/logger';
import {
  MAGIC_LINK_MIRROR_KEYS,
  clearMagicLinkMirror,
  readMagicLinkMirrorOrUnreadable,
  writeMagicLinkMirror,
} from '@utils/magicLinkMirror';

export const FIRST_SEND_ADDRESS_KEY = 'meetball_first_send_address';

/** Case-folded and trimmed: retyping your own address differently is not a different recipient. */
export const normalizeSendAddress = (email: string): string => email.trim().toLowerCase();

/**
 * The address the turned-down mark and the send-elsewhere signal were recorded under, kept for
 * this page life while an `/auth/link` rebind has left the tab unbound (Dexter's decision S strips
 * a payload that arrived with no binding), so the next rebind compares against it rather than
 * against the gap (F7 of #1274's delta review; `rebindFirstSendAddress`). Set by a rebind that
 * leaves the tab unbound. Cleared by a rebind that binds the tab, by whatever drops the flags
 * (`clearFirstSendAddress`, a rebind that moves the binding), and by a reload. NOT cleared by a
 * bind outside the rebind (the door's `rememberFirstSendAddress`), so it can still be set while
 * the tab is bound (#1281's delta F2); only a rebind that starts unbound reads it.
 */
let flagsRecordedUnder: string | null = null;

/**
 * ONE BIT of page life: whether this document has attempted a binding since the binding was last
 * taken down. F2 of #1275 step 1's adversarial review, Dexter's decision (2026-10-06): #1243's
 * page-life record, which held the address a refused write did not keep, also told Dexter's
 * decision S that a refused tab had a binding. Without it, a refused send, then `/auth/link` in
 * the same document taking a payload with no binding, then storage recovering, sent that payload
 * whole to the next address. Storage holds every write that landed, so "a binding was attempted
 * here" is all S needs beside it, and the address itself stays gone (#1275 step 1).
 *
 * THE SAME BIT STOPS LATER WRITES, Dexter's decision D40 (2026-10-06): once a page has tried and
 * failed to save an address, it saves none later in that page (`rememberFirstSendAddress`). A
 * whole payload stashed again at the door after a refused send, then storage recovering, let
 * the next send bind a stranger first and upload it whole (door PROBE-2 of the same review);
 * #1243's address swap used to write the first address there instead. Writing nothing leaves
 * the read-back empty, so the bind's gate strips that payload in place.
 *
 * Set by `rememberFirstSendAddress` when its guard reads storage as cleanly empty and it tries
 * the write, whatever the write then does, with the send attempt that set it
 * (`bindingAttemptedBy`); while it is set over an empty storage, no later call writes. Cleared by
 * `forgetBinding`, so by `clearFirstSendAddress` (the door's arrival with nothing pending) and by
 * every rebind, which reads it first; by `sendLink`'s undo of a rejected send only when that
 * send's own attempt set it (`releaseFirstSendAddress`, F1 of #1275 step 1's delta review r2);
 * and by a reload. Each of those takes a working tab's binding down at the same point. The
 * sign-out sweeps' key walk cannot reach it and need not: every sign-out ends in a document load.
 */
let hasAttemptedBinding = false;

/**
 * The send attempt that set `hasAttemptedBinding` (`newStrippedSendAttempt`), or null when it was
 * set by a bind that names no attempt (a claim, or `/auth/link`). Read only by
 * `releaseFirstSendAddress`, so a rejected send's undo takes back only a bit its own attempt set.
 */
let bindingAttemptedBy: string | null = null;

/** The stored binding, or null when there is none. THROWS when the read does. */
const readStoredFirstSendAddress = (): string | null => {
  const stored = globalThis.sessionStorage.getItem(FIRST_SEND_ADDRESS_KEY);
  return stored && stored.length > 0 ? stored : null;
};

/**
 * The address this tab first sent to, or null when it has not sent yet or the read fails.
 *
 * Storage is the only place a binding lives. A binding storage refused leaves nothing here: the
 * bind that did not land stripped the stash the tab held at that moment (`capabilityRuleFor` in
 * `AuthDoor.tsx`, #1275 step 1). That replaced #1243's page-life record, a module copy of the
 * refused address that a reload forgot while sessionStorage kept the whole stash (#1272, #1273).
 * A whole payload that enters the tab LATER in the same document sits over the missing binding,
 * and the attempted-bind bit covers it (`hasAttemptedBinding`, F2 of #1275 step 1's adversarial
 * review): `/auth/link`'s rebind counts it for Dexter's decision S, and the door's next bind
 * writes nothing (D40), reads back empty and strips.
 */
export const readFirstSendAddress = (): string | null => {
  try {
    return readStoredFirstSendAddress();
  } catch (error) {
    logger.debug('[magicLink] could not read the first send address', error);
    return null;
  }
};

/**
 * The binding as storage holds it, telling a read that THREW apart from an empty one: the
 * address, `null` when there is none, `undefined` when the read threw. The door's bind gates its
 * in-place strip on this (`capabilityRuleFor`): a read-back that threw is not a write that did not
 * land, since storage may hold the very address just written (F1 of #1275's adversarial review).
 * The `...OrUnreadable` shape of `readMagicLinkMirrorOrUnreadable` (#1253).
 */
export const readDurableFirstSendAddressOrUnreadable = (): string | null | undefined => {
  try {
    return readStoredFirstSendAddress();
  } catch (error) {
    logger.debug('[magicLink] could not read back the first send address', error);
    return undefined;
  }
};

/**
 * The binding as STORAGE holds it: what a reload would still find. Null when there is none or
 * the read fails. The same read as `readFirstSendAddress` now that nothing else can hold a
 * binding; the name is the read-back's, which every bind asks to learn whether its write landed.
 */
export const readDurableFirstSendAddress = (): string | null => {
  try {
    return readStoredFirstSendAddress();
  } catch (error) {
    logger.debug('[magicLink] could not read back the first send address', error);
    return null;
  }
};

/**
 * Records the address, and only the FIRST one: the binding is to the first
 * recipient, so a later send must not be able to move it by overwriting.
 *
 * A guard that cannot read REFUSES the write. It used to ask `readFirstSendAddress`,
 * which answers a failed read with null, "nothing bound yet", so a read that threw
 * over an existing binding let the write replace it: the binding moved to the
 * second address, and the bind's read-back then named that address the first
 * recipient, so the send that moved it carried the invite whole. Refused, the
 * read-back finds the old binding, or nothing, and strips either way. The mirror's
 * hydration goes through here too, so a read that fails there takes no binding, and
 * the hydration reads that back for `/auth/link` to fail closed on
 * (`hydrateFirstSendAddressFromMagicLinkMirror`).
 *
 * A write storage does not keep (it throws, or it lands nothing) leaves no binding, and
 * every caller reads the binding back to find that out: the door's binds and `/auth/link`'s
 * strip the tab's stash in place when nothing landed, and drop the claim entries filed under
 * no address. AND NO LATER CALL WRITES in that document (Dexter's decision D40): while
 * `hasAttemptedBinding` stands over an empty storage, this returns before the write, so a
 * payload stashed again after the refused send, and a send after storage recovers, still read
 * back empty and strip. Only what takes a working binding down (`forgetBinding`) or a reload
 * lets the next call write again.
 */
export const rememberFirstSendAddress = (normalized: string, attempt?: string): void => {
  try {
    if (readStoredFirstSendAddress() !== null) {
      return;
    }
  } catch (error) {
    logger.debug('[magicLink] could not read the first send address to record it', error);
    return;
  }
  // D40: an earlier attempt in this document did not land, so this one writes nothing either.
  if (hasAttemptedBinding) {
    return;
  }
  hasAttemptedBinding = true;
  bindingAttemptedBy = attempt ?? null;
  try {
    globalThis.sessionStorage.setItem(FIRST_SEND_ADDRESS_KEY, normalized);
  } catch (error) {
    logger.debug('[magicLink] could not record the first send address', error);
  }
};

/**
 * THE TURNED-DOWN MARK. It records that this tab has turned its first address down,
 * by the button or by sending elsewhere: "Wrong address?" over the binding, or a
 * send to an address other than the bound one. A social chip needs exactly that: it
 * cannot name the address it will sign in as, so it cannot be compared with the
 * binding, and once the first address has been turned down every chip strips the
 * stash it hands `/callback` (AuthDoor's `handleSocialExit`). The door's mount does
 * not read it, because "Wrong address?" alone sends nobody anything (the
 * send-elsewhere signal below). The door writes it at that click or at that send's
 * attempt, and only over a binding, since there is nothing to turn down without
 * one; a write that does not land fails closed, the door stripping the tab's stash
 * there and then (`turnDownFirstAddress`). It goes wherever the binding goes:
 * `clearFirstSendAddress` drops it with the binding, so the door's mount with
 * nothing pending and `sendLink`'s undo of a binding it created clear it too;
 * the sign-out sweeps list it; and `/auth/link`'s account-switch sweep keeps it
 * beside the binding (`storageCleanup.ts`). `/auth/link`'s rebind drops it only when
 * the binding MOVES from one bound address to a different one (`rebindFirstSendAddress`,
 * F3 of #1274's adversarial review and F7 of its delta review): it keeps it when the
 * tab's own flow comes back bound to the same address, and an unbound state on either
 * side is not a move. It is PER TAB, in sessionStorage and
 * never on the envelope, because it records what was said on this tab's screen: a
 * tab that hydrates the binding from the mirror has turned nothing down, and a send
 * to another address already strips what it mirrors. Nothing else un-sets it, so a
 * visitor who turns their own address down and then retypes it still gets the
 * stripped stash on a chip, and reopens the invite link.
 *
 * THE RESIDUAL it inherits from the binding, which survives into a later flow in
 * this tab that arrives carrying something (the door mount's note): a mark left from
 * an earlier flow in the same tab strips that later flow's own invite on the chips
 * too. What clears it is what clears the binding: a door arrival with nothing
 * pending, `sendLink`'s undo of a binding it created, the two sign-out sweeps
 * (`clearNonLogtoStorage`, `clearUserStorage`), and an `/auth/link` rebind that moves
 * the binding from one bound address to a different one. Closing the tab is NOT one of them:
 * reopening a closed tab, a restored session and "Duplicate tab" all bring its
 * sessionStorage back, and a 401 or an expired session runs no sweep at all. The
 * recovery is the ordinary one: sign in, then reopen the invite link.
 */
export const FIRST_SEND_TURNED_DOWN_KEY = 'meetball_first_send_turned_down';

/** Whether a per-tab flag (the mark, or the signal below) is stored. THROWS when the read does. */
const readStoredFlag = (key: string): boolean => globalThis.sessionStorage.getItem(key) !== null;

/** Whether the mark is set. A read that fails answers yes: the mark only ever strips. */
export const isFirstSendAddressTurnedDown = (): boolean => {
  try {
    return readStoredFlag(FIRST_SEND_TURNED_DOWN_KEY);
  } catch (error) {
    logger.debug('[magicLink] could not read the turned-down address', error);
    return true;
  }
};

/**
 * The ONE copy of how a per-tab flag over the binding is recorded, for the mark and for the
 * send-elsewhere signal below: only over a binding, then read back. Returns true only when it
 * can show the job is done, and false when the caller has to fail closed another way.
 * `markFirstSendAddressTurnedDown` says why each read answers the way it does.
 *
 * With no binding the flag is not written, and the two callers answer differently, so each
 * passes its own `answerWithoutBinding`. For the mark that is a done job (nothing to turn
 * down); for the signal it is not, because a send that went out stripped has already
 * happened and the flag that would tell the mount so cannot stand without a binding.
 * `value` is what is stored: the mark stores `'1'`, the signal the attempt that wrote it.
 */
const recordFlagOverBinding = (
  key: string,
  name: string,
  answerWithoutBinding: boolean,
  value: string,
): boolean => {
  try {
    if (readStoredFirstSendAddress() === null) {
      return answerWithoutBinding;
    }
  } catch (error) {
    logger.debug(`[magicLink] could not read the first send address to record ${name}`, error);
    return false;
  }
  try {
    globalThis.sessionStorage.setItem(key, value);
  } catch (error) {
    logger.debug(`[magicLink] could not record ${name}`, error);
  }
  try {
    return readStoredFlag(key);
  } catch (error) {
    logger.debug(`[magicLink] could not read back ${name}`, error);
    return false;
  }
};

/**
 * Records the mark, and only over a binding, then reads it back. Returns true only
 * when it can show the job is done: the mark reads back, or the binding reads as
 * absent, so there is nothing to turn down. Otherwise false, and the caller fails
 * closed another way: the write did not land, or a read failed. A failed read
 * means "not shown" here, both times: a binding read that fails cannot prove there
 * is nothing to turn down, and a read-back that fails cannot prove the write
 * landed. (`isFirstSendAddressTurnedDown` answers a failed read the other way,
 * because for the chip "turned down" is the stripping answer.) A refused write
 * alone is not a no: a mark an earlier click set still reads back.
 *
 * A tab whose storage refused its binding answers "nothing to turn down" here, and that is
 * true of its stash: the send whose bind did not land stripped it in place (#1275 step 1), so
 * a chip there hands `/callback` nothing whole to strip.
 */
export const markFirstSendAddressTurnedDown = (): boolean =>
  recordFlagOverBinding(FIRST_SEND_TURNED_DOWN_KEY, 'the turned-down address', true, '1');

/**
 * THE SEND-ELSEWHERE SIGNAL. It records that a send from this tab went out STRIPPED over
 * a binding: to an address other than the bound one (the mark's second writer), or a send
 * a failed binding read kept the door from seeing as one, or from reading back as the
 * bound address's own (`capabilityRuleFor`'s read-back). The door's mount reads it, and
 * nothing else, to decide whether it may re-arm the envelope from this tab's stash whole.
 * Such a send leaves the stash whole (the bound address keeps it) and writes the envelope
 * stripped for its recipient, so a mount after it must strip too, or it writes the whole
 * invite back over that envelope. A tab whose storage refused the binding has nothing to
 * write it over and needs nothing: the door strips its stash in place instead (the bind's
 * strip when the read-back answered empty, #1275 step 1, and `recordStrippedSend`'s
 * fallback either way), so its mount mirrors what is left.
 *
 * WHY NOT ONLY A SEND ELSEWHERE, which is what it used to record (F1 of #1245's
 * adversarial review). A send can go out stripped without counting as one, when a binding
 * read fails during it (Copilot 4176475670), and the remount that followed mirrored the
 * whole invite back over the envelope that send had stripped. A send that went out WHOLE
 * writes nothing: it went to the first recipient.
 *
 * WHY THE MARK CANNOT SAY THIS. "Wrong address?" sets the mark as well, with nobody
 * sent anything: the envelope is still the one the bound address's own send wrote, and
 * that address is still its only recipient. Both writers set the same mark because the
 * chips need both, so a mount that stripped on the mark stripped the first address's
 * own envelope after a bare press and a remount (N4 of the #1207 delta review). The
 * chips and the mark are unchanged; only the mount reads this.
 *
 * It keeps the mark's contract and its storage. Per tab, in sessionStorage, never on
 * the envelope. Written only over a binding (`recordFlagOverBinding`) and at the send's
 * attempt, like the mark and like that send's own mirror writes, so a send that then
 * fails does not undo it. The one exception is the 422 / pre-send undo, which knows no
 * token was minted and takes the signal back when that attempt is what wrote it
 * (`clearSentToAnotherAddress`; F2 of #1245's adversarial review). That is why the value
 * stored is the ATTEMPT, not a bare flag: a send "Wrong address?" detached can settle
 * after later sends, and the undo of its rejection must not take back a signal a later
 * stripped send wrote over it, which may have minted. A write that cannot
 * be shown landed, or a tab with no binding to write it over, fails closed in the door,
 * which strips the tab's stash in place (`recordStrippedSend`,
 * `turnDownFirstAddressBySendingElsewhere`). The in-place strip is the fallback because it
 * reaches every later mount AND outlives a reload. A read that fails answers yes. It goes
 * wherever the binding goes: `clearFirstSendAddress`, the sign-out sweeps, and kept
 * beside it by the account-switch sweep (`storageCleanup.ts`); `/auth/link`'s rebind
 * drops it, like the mark, only when the binding moves from one bound address to a
 * different one (`rebindFirstSendAddress`).
 */
export const FIRST_SEND_SENT_TO_ANOTHER_KEY = 'meetball_first_send_sent_to_another';

/**
 * Whether a send from this tab has gone out stripped. A read that fails answers yes: the
 * signal only ever strips.
 */
export const hasSentToAnotherAddress = (): boolean => {
  try {
    return readStoredFlag(FIRST_SEND_SENT_TO_ANOTHER_KEY);
  } catch (error) {
    logger.debug('[magicLink] could not read whether a send went out stripped', error);
    return true;
  }
};

/**
 * A value unique to one send attempt, for what that attempt records so its undo can tell its own
 * record from a later or earlier attempt's: the signal (see the key's note) and the
 * attempted-binding bit (`releaseFirstSendAddress`).
 */
export const newStrippedSendAttempt = (): string =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

/**
 * Records the signal for `attempt`, only over a binding, then reads it back. True only
 * when the signal reads back; false sends the caller to its fallback, as
 * `markFirstSendAddressTurnedDown` does, and so does a tab with no binding, where the mark
 * answers true: the stripped send this records has happened either way, and a mount that
 * cannot see it re-arms the whole stash. (With no binding, the send's bind did not land.
 * When its read-back answered empty, the bind has already stripped the stash in place, #1275
 * step 1, and the fallback strips the same stash again; when the read-back threw, the bind's
 * gate stays shut and the fallback is the only strip.)
 */
export const markSentToAnotherAddress = (attempt: string): boolean =>
  recordFlagOverBinding(FIRST_SEND_SENT_TO_ANOTHER_KEY, 'a stripped send', false, attempt);

/**
 * Takes the signal back if `attempt` is still what it holds, and nothing else: the binding
 * and the mark stay. For `sendLink`'s 422 / pre-send undo, which knows no token was
 * minted, so no link will read the stripped envelope that attempt mirrored. A signal a
 * later attempt wrote over it is not this attempt's to take back. A read or a removal
 * that fails leaves it set, which only ever strips.
 */
export const clearSentToAnotherAddress = (attempt: string): void => {
  try {
    if (globalThis.sessionStorage.getItem(FIRST_SEND_SENT_TO_ANOTHER_KEY) === attempt) {
      globalThis.sessionStorage.removeItem(FIRST_SEND_SENT_TO_ANOTHER_KEY);
    }
  } catch (error) {
    logger.debug('[magicLink] could not take back a stripped send', error);
  }
};

/** Forgets the binding, and nothing else. */
const forgetBinding = (): void => {
  hasAttemptedBinding = false;
  bindingAttemptedBy = null;
  try {
    globalThis.sessionStorage.removeItem(FIRST_SEND_ADDRESS_KEY);
  } catch (error) {
    logger.debug('[magicLink] could not clear the first send address', error);
  }
};

/**
 * Forgets the turned-down mark and the send-elsewhere signal, and the address they were recorded
 * under, and nothing else.
 */
const forgetFlagsOverBinding = (): void => {
  flagsRecordedUnder = null;
  try {
    globalThis.sessionStorage.removeItem(FIRST_SEND_TURNED_DOWN_KEY);
    globalThis.sessionStorage.removeItem(FIRST_SEND_SENT_TO_ANOTHER_KEY);
  } catch (error) {
    logger.debug('[magicLink] could not clear the flags over the first send address', error);
  }
};

/**
 * Forgets it, its turned-down mark and its send-elsewhere signal, so the next send starts a
 * fresh binding.
 */
export const clearFirstSendAddress = (): void => {
  forgetBinding();
  forgetFlagsOverBinding();
};

/**
 * REBINDS this tab for a payload that has just replaced its stash, and is the only way
 * `/auth/link` takes a binding down (#1274). The binding describes the payload the tab holds,
 * so the old binding goes before `bind` writes the one that describes the new payload.
 * Returns what `bind` returned.
 *
 * THE MARK AND THE SIGNAL GO ONLY IF THE ADDRESS MOVED, i.e. when this tab is bound after `bind`
 * to an address other than the one it was last bound to (F3 of #1274's adversarial review; for
 * "last", see ONLY BOUND STATES below).
 * Both record what this tab did under that address: the mark that it turned the address down,
 * the signal that a send from here went out stripped. Bound to the same address again, which is
 * what the tab's own flow coming back through `/auth/link` looks like, they still describe it,
 * and dropping the mark there let a chip hand `/callback` the whole invite the visitor had turned
 * that address down over. Bound to another address, they describe a flow that is no longer here,
 * and a kept signal made the door's next mount re-arm the new payload stripped, at its own
 * recipient's cost. "Bound" is what storage holds, on both sides of the comparison: no
 * page-life record stands in for a binding any more (#1275 step 1).
 *
 * ONLY BOUND STATES ARE COMPARED (F7 of #1274's delta review). Unbound is not an address: S's
 * strip leaves the tab unbound between the mount and the confirm, and counting that gap as a move
 * dropped the mark on a@, then none, then a@, so a chip handed `/callback` the whole invite. A
 * rebind that leaves the tab unbound moves nothing and keeps the flags, and carries the address
 * they were recorded under across the gap (`flagsRecordedUnder`); a rebind that starts unbound
 * compares against that address, so b@, then none, then a@ still drops them, as b@ to a@ does.
 * The carry lives in this document only, which the mount and the confirm share. A reload loses
 * it, and the next rebind over an unbound tab then compares nothing and keeps the flags, which
 * only ever strip; so does a binding read that throws. The door binds through
 * `rememberFirstSendAddress`, never through here, so a carry outliving its gap is read only while
 * the tab is unbound again.
 *
 * `bind` IS HANDED THE OLD BINDING, from the one read of storage made here before it is taken
 * down. A read that throws hands `undefined`, "could not be read", which is not "unbound": the
 * landing strips a payload that arrives with no binding in a tab that had one (Dexter's decision
 * S on #1274), and a binding it cannot rule out counts. The flag comparison above compares
 * nothing on that throw. An EMPTY read hands `undefined` too when this document has attempted a
 * binding since the last one was taken down (`hasAttemptedBinding`): a write storage refused
 * leaves the read empty, and the tab had still sent (F2 of #1275 step 1's adversarial review).
 * The flag comparison does not use that bit: storage alone says what was bound.
 */
export const rebindFirstSendAddress = <T>(
  bind: (priorBinding: string | null | undefined) => T,
): T => {
  let priorBinding: string | null | undefined;
  try {
    priorBinding = readStoredFirstSendAddress();
  } catch (error) {
    logger.debug('[magicLink] could not read the first send address to rebind it', error);
    priorBinding = undefined;
  }
  const boundBefore = priorBinding === null ? flagsRecordedUnder : priorBinding;
  const bindingForBind = priorBinding === null && hasAttemptedBinding ? undefined : priorBinding;
  forgetBinding();
  const result = bind(bindingForBind);
  const boundAfter = readFirstSendAddress();
  if (boundAfter === null) {
    flagsRecordedUnder = boundBefore ?? null;
  } else {
    if (typeof boundBefore === 'string' && boundAfter !== boundBefore) {
      forgetFlagsOverBinding();
    }
    flagsRecordedUnder = null;
  }
  return result;
};

/**
 * `sendLink`'s undo of a send rejected with nothing minted (a 422, or a rejection that never left
 * the browser): forgets the binding, its mark and its signal, as `clearFirstSendAddress` does, and
 * the attempted-binding bit ONLY when `attempt` set it. F1 of #1275 step 1's delta review r2: in a
 * tab whose storage refused the binding the undo runs on every rejected send (no binding before
 * it), so a LATER rejected send took back the bit an EARLIER refused send set, the page bound
 * again, and a stranger's send carried a whole invite stashed again since (reopening D39 and D40).
 * A bit an earlier attempt set stays, with the attempt that set it.
 */
export const releaseFirstSendAddress = (attempt: string): void => {
  const earlierAttempt = hasAttemptedBinding && bindingAttemptedBy !== attempt;
  const attemptedBy = bindingAttemptedBy;
  clearFirstSendAddress();
  if (earlierAttempt) {
    hasAttemptedBinding = true;
    bindingAttemptedBy = attemptedBy;
  }
};

/**
 * Copies the binding onto the cross-tab envelope, beside the intent and the
 * destination it binds. Called from the door's mount and from every send, i.e.
 * exactly where the other three mirrors are written.
 *
 * An ABSENT binding CLEARS the mirror, like the other writers: the door's mount
 * drops the per-tab key when nothing is pending, and the envelope has to say the
 * same thing the tab does or the two disagree about which flow is live.
 *
 * A tab whose storage refused its binding mirrors none, and nothing whole travels beside
 * that null: the send's own payload writes are stripped by its bind's read-back, and the
 * tab's stash is stripped in place, which is what any later mount mirrors (#1275 step 1).
 * A tab that hydrates that envelope reads any address as the first recipient of a
 * payload with no capability left in it.
 */
export const mirrorFirstSendAddressForMagicLink = (): void => {
  writeMagicLinkMirror(MAGIC_LINK_MIRROR_KEYS.firstSendAddress, readFirstSendAddress());
};

/**
 * What hydrating the mirrored binding left this tab with, which is what `/auth/link`'s
 * mount needs to know to fail closed:
 *
 * - `absent`: the envelope carried no binding this tab can use, and the binding key read
 *   cleanly: no key, an empty value, or a value the envelope's decode rejects (an expired
 *   envelope, a wrong shape, not JSON). A legitimate recipient's own link arrives
 *   this way when a second door tab, holding the
 *   same invite with nothing sent, mirrored the invite whole on mount and in that same
 *   mount removed the binding key the first tab's send wrote. Nothing to fail closed on:
 *   the landing's confirm binds the link's own address later, and stripping would cost
 *   that recipient its invite. The exception is the landing's, not this function's: when
 *   the envelope also MOVED a payload into a tab that had a binding of its own, the landing
 *   strips it as on `refused` (Dexter's decision S on #1274; see `rebindFirstSendAddress`).
 * - `durable`: storage holds a binding now, the mirrored address or the tab's own first
 *   one, which first-writer-wins keeps over the envelope's.
 * - `refused`: storage holds no binding, and either the envelope carried one (the guard
 *   could not read, or the write did not land) or the read of its key threw, so
 *   nothing shows that it carried none. Whatever the envelope bound is in this tab with
 *   nothing binding it, and the caller has to fail closed another way.
 */
export type MirroredBindingHydration = 'absent' | 'durable' | 'refused';

/** The binding's own check, the shape the mirror's writer stores: a non-empty address. */
const isMirroredBinding = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

/**
 * Copies the mirrored binding into THIS tab and drops the mirror, exactly as the
 * other three hydrators do. Called once, by `/auth/link`, beside them.
 *
 * A tab that already has its own binding keeps it: `rememberFirstSendAddress` is
 * first-writer-wins. That is right only while the tab still holds the payload its binding
 * describes, so `/auth/link` calls this inside `rebindFirstSendAddress`, which takes the old
 * binding down first, whenever the envelope replaced the tab's intent or destination (#1274).
 * What this keeps is a binding over an envelope that moved no payload; a replaced payload
 * takes the envelope's binding. When the envelope carries none, a tab that had a binding is
 * left with none and the landing strips the payload in place, as on `refused` (Dexter's
 * decision S on #1274); a fresh tab keeps it whole, the abandoned-door residual.
 *
 * It READS THE BINDING BACK, from storage alone, as the door's bind does, and answers
 * with what it found: the guard refuses the write on a read that fails, so binding blind
 * would let a tab take delivery of the invite and no binding at all, and a send from it to
 * a stranger would bind the stranger first and upload the invite whole (N5 of the #1207
 * delta review).
 *
 * THE MIRROR IS DROPPED ON A REFUSED WRITE TOO, deliberately. The caller strips the
 * tab's stash on `refused` and no later bind puts the invite back, so a kept envelope
 * buys this tab nothing. The other three hydrators drop theirs whatever their writes
 * do, so a kept binding would guard nothing on the envelope, and the next
 * `/auth/link` in this browser profile would hydrate it into an unrelated flow, where
 * first-writer-wins binds THAT flow's invite to this address. And a StrictMode second
 * mount has to find it gone and answer `absent`, as the other three find theirs.
 *
 * A BINDING IT COULD NOT READ is not a binding the envelope lacked, so it answers as a write
 * that did not land: `refused` unless storage already holds a binding of this tab's own,
 * which first-writer-wins would have kept over the envelope's anyway.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: F1 of #1253's adversarial review (Copilot 4177018583),
 * S1 of its r3. The binding key is read ONCE, through `readMagicLinkMirrorOrUnreadable`: a
 * second read can disagree with the first, so a read that threw after a clean first one
 * would answer `absent` and the landing would keep the invite whole with no binding. A
 * thrown read is unreadable (`refused`, or `durable` if storage already holds a binding).
 * Anything it did read is decided by the shared decode, so a missing, empty, expired,
 * wrong-shape or non-JSON value is `absent`.
 */
export const hydrateFirstSendAddressFromMagicLinkMirror = (): MirroredBindingHydration => {
  const mirrored = readMagicLinkMirrorOrUnreadable(
    MAGIC_LINK_MIRROR_KEYS.firstSendAddress,
    isMirroredBinding,
  );
  clearMagicLinkMirror(MAGIC_LINK_MIRROR_KEYS.firstSendAddress);

  if (mirrored === null) {
    return 'absent';
  }
  if (mirrored !== undefined) {
    rememberFirstSendAddress(mirrored);
  }
  return readDurableFirstSendAddress() === null ? 'refused' : 'durable';
};

/**
 * Test seam: what a document reload does to this module. The flags' carry
 * (`flagsRecordedUnder`), the attempted-binding bit (`hasAttemptedBinding`) and the attempt that
 * set it (`bindingAttemptedBy`) go, and storage stays. Nothing in the app calls this.
 */
export const resetFirstSendAddressPageLifeForTests = (): void => {
  flagsRecordedUnder = null;
  hasAttemptedBinding = false;
  bindingAttemptedBy = null;
};
